import { useState, type ReactElement } from "react";
import {
  useCanAccess,
  useCreate,
  useGetIdentity,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import * as QRCode from "qrcode";
import {
  Archive,
  BarChart3,
  CircleHelp,
  Copy,
  ExternalLink,
  Link2,
  Pause,
  Pencil,
  Play,
  Plus,
  QrCode,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type {
  TrackingLinkExperience,
  TrackingLinkStatus,
  TrackingLinkSummary,
} from "../types";
import {
  TRACKING_LINK_UTM_EXAMPLE,
  slugifyTrackingLink,
  trackingLinkMessage,
  trackingLinkPreviewUrl,
  trackingLinkSlugAvailability,
  trackingLinkSlugError,
  trackingLinkUrl,
} from "./trackingLinkContract";

type LinkFormState = {
  name: string;
  slug: string;
  description: string;
  experience: TrackingLinkExperience;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  expires_at: string;
};

const LIVE_SLUG_CONFLICT_MESSAGE =
  "This slug belongs to an active or paused link. Archive that link to free the slug, or pick another one.";

const EMPTY_FORM: LinkFormState = {
  name: "",
  slug: "",
  description: "",
  experience: "b2c",
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
  utm_content: "",
  expires_at: "",
};

export function TrackingLinksPage() {
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "configuration",
    action: "edit",
  });
  const [create, { isPending: isCreating }] = useCreate();
  const [update, { isPending: isUpdating }] = useUpdate();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TrackingLinkSummary | null>(null);

  const {
    data: links = [],
    isPending,
    isFetching,
    error,
    refetch,
  } = useGetList<TrackingLinkSummary>("tracking_links_summary", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "created_at", order: "DESC" },
    filter: {},
  });
  const mutateStatus = (
    link: TrackingLinkSummary,
    status: TrackingLinkStatus,
  ) => {
    update(
      "tracking_links",
      {
        id: link.id,
        data: { status },
        previousData: link,
      },
      {
        onSuccess: () => {
          notify(`Link ${status === "active" ? "activated" : status}.`);
          refresh();
        },
        onError: (mutationError) =>
          notify(String(mutationError), { type: "error" }),
      },
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-5 pb-20">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Link2 className="size-6" /> Tracking links
          </h1>
          <p className="text-sm text-muted-foreground max-w-3xl mt-1">
            Branded links that count the WhatsApp handoff and carry a visible,
            deterministic source code into Felix.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={`size-4 ${isFetching ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
            Refresh
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Create link
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">All links</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <p className="text-sm text-destructive">
              Could not load tracking links: {String(error)}
            </p>
          ) : null}
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Link</TableHead>
                  <TableHead>Creator</TableHead>
                  <TableHead>Experience</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Redirects</TableHead>
                  <TableHead className="text-right">New contacts</TableHead>
                  <TableHead className="text-right">
                    Attributed Echoes
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.map((link) => {
                  const canEdit =
                    isAdmin === true ||
                    String(link.created_by_sales_id) === String(identity?.id);
                  return (
                    <TableRow key={String(link.id)}>
                      <TableCell className="min-w-[18rem]">
                        <div className="font-medium">{link.name}</div>
                        <a
                          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                          href={trackingLinkPreviewUrl(link.slug)}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`Preview ${link.name}; preview redirects are excluded from counted clicks`}
                        >
                          {trackingLinkUrl(link.slug)}
                          <ExternalLink className="size-3" />
                        </a>
                        <div className="text-xs text-muted-foreground">
                          Safe preview — does not count as a person click
                        </div>
                        <div className="text-xs font-mono text-muted-foreground mt-1">
                          {link.public_code}
                        </div>
                      </TableCell>
                      <TableCell>
                        {link.created_by_name || "CRM user"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {link.experience === "real_estate"
                            ? "Real estate"
                            : "B2C Echo"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={link.status}
                          expiresAt={link.expires_at}
                        />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <div>{link.human_clicks ?? 0} counted</div>
                        <div className="text-xs text-muted-foreground">
                          {link.preview_clicks ?? 0} previews excluded
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {link.new_contacts ?? 0}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <div>{link.echoes_started ?? 0} started</div>
                        <div className="text-xs text-muted-foreground">
                          {link.echoes_completed ?? 0} completed
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <ActionTooltip
                            label={`View analytics for ${link.name}`}
                          >
                            <Button variant="ghost" size="icon" asChild>
                              <Link
                                to={`/tracking-links/${encodeURIComponent(String(link.id))}/analytics`}
                                aria-label={`View analytics for ${link.name}`}
                              >
                                <BarChart3 className="size-4" />
                              </Link>
                            </Button>
                          </ActionTooltip>
                          <ActionTooltip
                            label={`Copy countable link ${link.name}`}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Copy countable link ${link.name}`}
                              onClick={() => copyLink(link, notify)}
                            >
                              <Copy className="size-4" />
                            </Button>
                          </ActionTooltip>
                          <ActionTooltip
                            label={`Download QR code for ${link.name}`}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Download QR code for ${link.name}`}
                              onClick={() => downloadQr(link, notify)}
                            >
                              <QrCode className="size-4" />
                            </Button>
                          </ActionTooltip>
                          {canEdit ? (
                            <>
                              <ActionTooltip label={`Edit ${link.name}`}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Edit ${link.name}`}
                                  onClick={() => setEditing(link)}
                                >
                                  <Pencil className="size-4" />
                                </Button>
                              </ActionTooltip>
                              {link.status === "active" ? (
                                <ActionTooltip label={`Pause ${link.name}`}>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Pause ${link.name}`}
                                    disabled={isUpdating}
                                    onClick={() => mutateStatus(link, "paused")}
                                  >
                                    <Pause className="size-4" />
                                  </Button>
                                </ActionTooltip>
                              ) : link.status === "paused" ? (
                                <ActionTooltip label={`Activate ${link.name}`}>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Activate ${link.name}`}
                                    disabled={isUpdating}
                                    onClick={() => mutateStatus(link, "active")}
                                  >
                                    <Play className="size-4" />
                                  </Button>
                                </ActionTooltip>
                              ) : null}
                              {link.status !== "archived" ? (
                                <ActionTooltip label={`Archive ${link.name}`}>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Archive ${link.name}`}
                                    disabled={isUpdating}
                                    onClick={() =>
                                      mutateStatus(link, "archived")
                                    }
                                  >
                                    <Archive className="size-4" />
                                  </Button>
                                </ActionTooltip>
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!links.length ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-28 text-center">
                      {isPending
                        ? "Loading links…"
                        : "No tracking links yet. Create the first branded Felix link."}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <TrackingLinkDialog
        key={createOpen ? "create-open" : "create-closed"}
        open={createOpen}
        onOpenChange={setCreateOpen}
        busy={isCreating}
        existingLinks={links}
        onSave={(form) => {
          const slugError = trackingLinkSlugError(form.slug);
          if (slugError) {
            notify(slugError, { type: "warning" });
            return;
          }
          if (
            trackingLinkSlugAvailability(form.slug, links) === "live_conflict"
          ) {
            notify(LIVE_SLUG_CONFLICT_MESSAGE, { type: "warning" });
            return;
          }
          create(
            "tracking_links",
            {
              data: {
                ...nullableFormFields(form),
                message_template:
                  form.experience === "real_estate"
                    ? "real_estate_default"
                    : "b2c_default",
                status: "active",
              },
            },
            {
              onSuccess: () => {
                notify("Tracking link created.");
                setCreateOpen(false);
                refresh();
              },
              onError: (mutationError) =>
                notify(readableMutationError(mutationError), {
                  type: "error",
                }),
            },
          );
        }}
      />

      <TrackingLinkDialog
        key={String(editing?.id ?? "none")}
        open={editing != null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={isUpdating}
        existing={editing ?? undefined}
        onSave={(form) => {
          if (!editing) return;
          update(
            "tracking_links",
            {
              id: editing.id,
              data: {
                name: form.name.trim(),
                description: form.description.trim() || null,
                experience: form.experience,
                message_template:
                  form.experience === "real_estate"
                    ? "real_estate_default"
                    : "b2c_default",
                utm_source: form.utm_source.trim() || null,
                utm_medium: form.utm_medium.trim() || null,
                utm_campaign: form.utm_campaign.trim() || null,
                utm_content: form.utm_content.trim() || null,
                expires_at: form.expires_at
                  ? new Date(form.expires_at).toISOString()
                  : null,
              },
              previousData: editing,
            },
            {
              onSuccess: () => {
                notify("Tracking link updated.");
                setEditing(null);
                refresh();
              },
              onError: (mutationError) =>
                notify(readableMutationError(mutationError), {
                  type: "error",
                }),
            },
          );
        }}
      />
    </div>
  );
}

TrackingLinksPage.path = "/tracking-links";

function ActionTooltip({
  label,
  children,
}: {
  label: string;
  children: ReactElement;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function TrackingLinkDialog({
  open,
  onOpenChange,
  onSave,
  busy,
  existing,
  existingLinks = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (form: LinkFormState) => void;
  busy: boolean;
  existing?: TrackingLinkSummary;
  existingLinks?: readonly TrackingLinkSummary[];
}) {
  const [form, setForm] = useState<LinkFormState>(() =>
    existing
      ? {
          name: existing.name,
          slug: existing.slug,
          description: existing.description ?? "",
          experience: existing.experience,
          utm_source: existing.utm_source ?? "",
          utm_medium: existing.utm_medium ?? "",
          utm_campaign: existing.utm_campaign ?? "",
          utm_content: existing.utm_content ?? "",
          expires_at: toDateTimeLocal(existing.expires_at),
        }
      : EMPTY_FORM,
  );
  const [slugTouched, setSlugTouched] = useState(Boolean(existing));
  const publicCode = existing?.public_code ?? "EF-XXXXXXXX";
  const slugError = trackingLinkSlugError(form.slug);
  const slugAvailability = existing
    ? "available"
    : trackingLinkSlugAvailability(form.slug, existingLinks);
  const valid =
    Boolean(form.name.trim()) &&
    !slugError &&
    slugAvailability !== "live_conflict";

  const setField = <K extends keyof LinkFormState>(
    key: K,
    value: LinkFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {existing ? "Edit tracking link" : "Create tracking link"}
          </DialogTitle>
          <DialogDescription>
            The public slug is immutable after creation. Felix receives the
            separate EF code shown in the message preview.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="tracking-link-name">Name</Label>
            <Input
              id="tracking-link-name"
              maxLength={120}
              value={form.name}
              onChange={(event) => {
                const name = event.target.value;
                setForm((current) => ({
                  ...current,
                  name,
                  slug: slugTouched ? current.slug : slugifyTrackingLink(name),
                }));
              }}
              placeholder="Echoflicks Linked Post"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="tracking-link-slug">Short URL slug</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {trackingLinkUrl("").replace(/\/$/, "")}/
              </span>
              <Input
                id="tracking-link-slug"
                value={form.slug}
                disabled={Boolean(existing)}
                placeholder="echoflicks-linked-post"
                onChange={(event) => {
                  setSlugTouched(true);
                  setField("slug", slugifyTrackingLink(event.target.value));
                }}
              />
            </div>
            {slugError ? (
              <p className="text-xs text-destructive">{slugError}</p>
            ) : slugAvailability === "live_conflict" ? (
              <p className="text-xs text-destructive">
                {LIVE_SLUG_CONFLICT_MESSAGE}
              </p>
            ) : slugAvailability === "archived_reuse" ? (
              <p className="text-xs text-muted-foreground">
                This slug was used by an archived link. The new link gets its
                own EF code and starts at zero clicks; the archived link keeps
                its old numbers.
              </p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="tracking-link-description">Description</Label>
            <Textarea
              id="tracking-link-description"
              maxLength={500}
              value={form.description}
              onChange={(event) => setField("description", event.target.value)}
              placeholder="Track visits and Echoes from the Echoflicks Linked Post"
            />
          </div>
          <div className="grid gap-2">
            <Label>Experience</Label>
            <Select
              value={form.experience}
              onValueChange={(value) =>
                setField("experience", value as TrackingLinkExperience)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="b2c">B2C Echo</SelectItem>
                <SelectItem value="real_estate">Real estate</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Experience selects Felix&apos;s friendly B2C or real-estate entry
              flow. It does not grant CRM permissions.
            </p>
          </div>
          <div className="rounded-md border bg-muted/40 p-3">
            <p className="text-xs font-medium mb-1">WhatsApp message preview</p>
            <p className="text-sm">
              {trackingLinkMessage(form.experience, publicCode)}
            </p>
          </div>

          <div className="grid gap-3 rounded-md border p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-medium">UTM tracking (optional)</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  UTMs are labels that tell the CRM where this link was shared.
                  They organize reporting and do not change the WhatsApp
                  message.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    utm_source: TRACKING_LINK_UTM_EXAMPLE.source,
                    utm_medium: TRACKING_LINK_UTM_EXAMPLE.medium,
                    utm_campaign: TRACKING_LINK_UTM_EXAMPLE.campaign,
                    utm_content: TRACKING_LINK_UTM_EXAMPLE.content,
                  }))
                }
              >
                <Sparkles className="size-4" />
                Use Linked Post example
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <UtmField
                id="utm-source"
                label="UTM source"
                explanation="Where the visitor found the link: a platform, publication, partner, or newsletter."
                example={TRACKING_LINK_UTM_EXAMPLE.source}
                value={form.utm_source}
                onChange={(value) => setField("utm_source", value)}
              />
              <UtmField
                id="utm-medium"
                label="UTM medium"
                explanation="The broad marketing channel, such as social, email, QR, print, or partner."
                example={TRACKING_LINK_UTM_EXAMPLE.medium}
                value={form.utm_medium}
                onChange={(value) => setField("utm_medium", value)}
              />
              <UtmField
                id="utm-campaign"
                label="UTM campaign"
                explanation="The initiative or promotion this link belongs to. Reuse the same campaign value across related links."
                example={TRACKING_LINK_UTM_EXAMPLE.campaign}
                value={form.utm_campaign}
                onChange={(value) => setField("utm_campaign", value)}
              />
              <UtmField
                id="utm-content"
                label="UTM content"
                explanation="An optional label that distinguishes versions or placements within the same campaign."
                example={TRACKING_LINK_UTM_EXAMPLE.content}
                value={form.utm_content}
                onChange={(value) => setField("utm_content", value)}
              />
            </div>

            <UtmPreview form={form} />
          </div>
          <div className="grid gap-2 sm:max-w-xs">
            <Label htmlFor="tracking-link-expires">Expires (optional)</Label>
            <Input
              id="tracking-link-expires"
              type="datetime-local"
              value={form.expires_at}
              onChange={(event) => setField("expires_at", event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              After this time the link returns an unavailable page and records
              no click.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!valid || busy} onClick={() => onSave(form)}>
            {busy ? "Saving…" : existing ? "Save changes" : "Create link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UtmField({
  id,
  label,
  explanation,
  example,
  value,
  onChange,
}: {
  id: string;
  label: string;
  explanation: string;
  example: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <div className="flex items-center gap-1.5">
        <Label htmlFor={id}>{label}</Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`What is ${label}?`}
              className="rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CircleHelp className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs" sideOffset={6}>
            {explanation}
          </TooltipContent>
        </Tooltip>
      </div>
      <Input
        id={id}
        maxLength={160}
        placeholder={example}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="text-xs text-muted-foreground">
        Example: <code>{example}</code>
      </p>
    </div>
  );
}

function UtmPreview({ form }: { form: LinkFormState }) {
  const values = {
    source: form.utm_source.trim(),
    medium: form.utm_medium.trim(),
    campaign: form.utm_campaign.trim(),
    content: form.utm_content.trim(),
  };
  const configured = Object.values(values).some(Boolean);
  const preview = configured ? values : TRACKING_LINK_UTM_EXAMPLE;

  return (
    <div className="rounded-md bg-muted/40 p-3">
      <p className="text-xs font-medium">
        {configured
          ? "Your attribution preview"
          : "Example attribution preview"}
      </p>
      <p className="mt-1 text-sm">
        {configured ? "This link" : "“Echoflicks Linked Post”"} will be reported
        as coming from <strong>{preview.source || "not set"}</strong>
        {" via "}
        <strong>{preview.medium || "not set"}</strong>, under campaign{" "}
        <strong>{preview.campaign || "not set"}</strong>
        {preview.content ? (
          <>
            , with content variant <strong>{preview.content}</strong>
          </>
        ) : null}
        .
      </p>
      {!configured ? (
        <p className="mt-1 text-xs text-muted-foreground">
          These are preview values only. Choose “Use Linked Post example” to
          fill and save them, or enter your own labels.
        </p>
      ) : null}
    </div>
  );
}

function StatusBadge({
  status,
  expiresAt,
}: {
  status: TrackingLinkStatus;
  expiresAt?: string | null;
}) {
  const expired = Boolean(
    expiresAt && new Date(expiresAt).getTime() <= Date.now(),
  );
  return (
    <Badge variant={status === "active" && !expired ? "default" : "secondary"}>
      {expired ? "expired" : status}
    </Badge>
  );
}

function nullableFormFields(form: LinkFormState) {
  return {
    name: form.name.trim(),
    slug: form.slug,
    description: form.description.trim() || null,
    experience: form.experience,
    utm_source: form.utm_source.trim() || null,
    utm_medium: form.utm_medium.trim() || null,
    utm_campaign: form.utm_campaign.trim() || null,
    utm_content: form.utm_content.trim() || null,
    expires_at: form.expires_at
      ? new Date(form.expires_at).toISOString()
      : null,
  };
}

function toDateTimeLocal(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

async function copyLink(
  link: TrackingLinkSummary,
  notify: ReturnType<typeof useNotify>,
) {
  try {
    await navigator.clipboard.writeText(trackingLinkUrl(link.slug));
    notify("Link copied.");
  } catch (error) {
    notify(`Could not copy link: ${String(error)}`, { type: "error" });
  }
}

async function downloadQr(
  link: TrackingLinkSummary,
  notify: ReturnType<typeof useNotify>,
) {
  try {
    const dataUrl = await QRCode.toDataURL(trackingLinkUrl(link.slug), {
      width: 1024,
      margin: 3,
      errorCorrectionLevel: "H",
    });
    const anchor = document.createElement("a");
    anchor.href = dataUrl;
    anchor.download = `${link.slug}-qr.png`;
    anchor.click();
  } catch (error) {
    notify(`Could not generate QR code: ${String(error)}`, { type: "error" });
  }
}

function readableMutationError(error: unknown): string {
  const text = String(error);
  if (text.includes("23505") || text.toLowerCase().includes("duplicate")) {
    return LIVE_SLUG_CONFLICT_MESSAGE;
  }
  return text;
}
